import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", phone: "+91 ", specialisation: "" };

function Row({ label, children }) {
  return <div><Label className="font-semibold">{label}</Label><div className="mt-1">{children}</div></div>;
}

// tech === "new" → create; tech object → edit (password optional).
export default function TechnicianFormDialog({ tech, onClose, onSaved }) {
  const isNew = tech === "new";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!tech) { return; }
    setForm(isNew ? EMPTY : { name: tech.name, email: tech.email, password: "", phone: tech.phone || "", specialisation: tech.specialisation || "" });
  }, [tech, isNew]);

  if (!tech) return null;

  const save = async () => {
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 10) { toast.error("Name and a valid phone are required"); return; }
    if (isNew && (!form.email.trim() || form.password.length < 6)) { toast.error("Email and a password of 6+ characters are required"); return; }
    if (!isNew && form.password && form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      if (isNew) {
        await api.post("/admin/technicians", form);
      } else {
        const { email, password, ...rest } = form;
        await api.patch(`/admin/technicians/${tech.id}`, password ? { ...rest, password } : rest);
      }
      toast.success(isNew ? "Technician created" : "Technician updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!tech} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto" data-testid="tech-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isNew ? "Add technician" : "Edit technician"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Row label="Full name *"><Input data-testid="tech-form-name" value={form.name} onChange={set("name")} className="border border-slate-200 h-11" /></Row>
          <Row label="Email *"><Input data-testid="tech-form-email" type="email" disabled={!isNew} value={form.email} onChange={set("email")} className="border border-slate-200 h-11" /></Row>
          <Row label={isNew ? "Password *" : "New password (leave blank to keep)"}>
            <Input data-testid="tech-form-password" type="password" value={form.password} onChange={set("password")} className="border border-slate-200 h-11" />
          </Row>
          <Row label="Phone number *"><Input data-testid="tech-form-phone" value={form.phone} onChange={set("phone")} className="border border-slate-200 h-11" /></Row>
          <Row label="Specialisation"><Input data-testid="tech-form-specialisation" placeholder="e.g. Laptop, Network, Printer" value={form.specialisation} onChange={set("specialisation")} className="border border-slate-200 h-11" /></Row>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={loading} data-testid="tech-form-save-btn" className="bg-brand hover:bg-brand-dark text-white font-bold shadow-btn">
            {loading ? "Saving…" : isNew ? "Create technician" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
